import { Stack, Text } from '@mantine/core';

/**
 * What a bill was for, on the two lists that show bills.
 *
 * A bill reaches this business two ways. A delivery opens one as a side effect of the goods being
 * received, and it is explained by its receipt lines. A bill for the propane, the ice or the
 * drinking water has no goods behind it and is explained only by the sentence somebody typed.
 *
 * The column that shows this used to be headed "Invoice number", which was honest and nearly
 * always blank — island vendors mostly do not give one. Renaming it "What for" made it honest for
 * the propane bill and dishonest for every other row: six deliveries in a row answering "—" to a
 * column asking what they were for, on the one screen whose whole job is being checkable against
 * the paperwork in the owner's hand. A delivery-backed bill DOES have an answer, and the answer is
 * that it was a delivery — `receiptId` says so, and saying so is what lets the reader tell the two
 * kinds apart at a glance and know which ones have a receipt to go and look at.
 *
 * Written once and used from both tables because they had already drifted: the vendor's page put
 * the invoice number first and the payables list put the description first, so the same bill read
 * differently depending on which screen you found it on.
 */
export interface BillSubjectFields {
  description?: string | null;
  invoiceNumber?: string | null;
  /** Set when a delivery opened this bill. Null on a bill that stands on its own. */
  receiptId?: string | null;
}

/** The one-line answer, for a notification or a modal title where there is no room for two. */
export function billSubject(bill: BillSubjectFields): string {
  const what = typeof bill.description === 'string' ? bill.description.trim() : '';
  if (what) return what;
  if (typeof bill.receiptId === 'string' && bill.receiptId) return 'A delivery';
  const number = typeof bill.invoiceNumber === 'string' ? bill.invoiceNumber.trim() : '';
  return number || '—';
}

/**
 * The table cell: what it was for, with the invoice number underneath when there is one — the
 * number is what gets matched against a statement, so it is kept, but it is not what tells a
 * person which bill they are looking at.
 */
export function BillSubject({ bill, strong = false }: { bill: BillSubjectFields; strong?: boolean }) {
  const subject = billSubject(bill);
  const number = typeof bill.invoiceNumber === 'string' ? bill.invoiceNumber.trim() : '';
  return (
    <Stack gap={0}>
      <Text size="sm" fw={strong ? 600 : undefined}>
        {subject}
      </Text>
      {number && number !== subject && (
        <Text size="xs" c="dimmed">
          {number}
        </Text>
      )}
    </Stack>
  );
}
